import React from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import { Tooltip, PillText } from '@web/atoms';
import checkIcon from '../../images/Icons/check-in-circle-lilac.svg';
import {
  Card,
  CardWrapper,
  Info,
  StyledImage,
  Name,
  Description,
  CardContent,
  CardHeader,
  MoreDetails,
  BadgeWrapper,
  IconContainer,
} from './styles.js';

export const RecommendedProductCard = ({
  label,
  title,
  badge,
  badgeVariant = 'dark',
  description,
  checked = false,
  img,
  imgSize,
  onChange,
  onDetailsClick,
  showMoreDetails,
  unavailable,
  unavailableText = 'Unavailable due to allergy concern',
  notSelectable,
  notSelectableText = 'To choose this item, please remove one of your previous selections.',
  level,
  orientation = 'row',
  mobileOrientation,
  offsetBadge = false,
  width = 345,
  mobileWidth,
  bgColor = 'lightest',
  typography = 'heading4',
  showCheckmark = false,
  alignColumnContent = 'left',
  borderRadius = 24,
  outOfStock = false,
}) => {
  const imgWidth = imgSize ?? img.width ?? 72;
  const imgHeight = imgSize ?? img.height ?? 72;

  const getProductCard = props => (
    <CardWrapper $offsetBadge={offsetBadge ? 10 : 0}>
      {outOfStock ? (
        <BadgeWrapper $outOfStock={outOfStock}>
          {' '}
          <PillText
            text={'OUT OF STOCK'}
            variant="disabled"
            size="large"
          ></PillText>
        </BadgeWrapper>
      ) : (
        offsetBadge &&
        badge && (
          <BadgeWrapper>
            <PillText text={badge} variant={badgeVariant} size="large" />
          </BadgeWrapper>
        )
      )}
      <Card
        $orientation={orientation}
        $mobileOrientation={mobileOrientation ?? orientation}
        {...props}
        checked={checked}
        width={width}
        $mobileWidth={mobileWidth}
        unavailable={unavailable || outOfStock}
        $notSelectable={notSelectable}
        $bgColor={bgColor}
        aria-label={`${label} ${title}`}
        onClick={!notSelectable && !outOfStock && onChange}
        $offsetBadge={offsetBadge}
        $borderRadius={borderRadius}
      >
        <CardContent
          $orientation={orientation}
          $mobileOrientation={mobileOrientation ?? orientation}
          $offsetBadge={offsetBadge && badge}
          $alignColumnContent={alignColumnContent}
        >
          <StyledImage
            checked={checked}
            unavailable={unavailable || outOfStock}
            $orientation={orientation}
            $mobileOrientation={mobileOrientation ?? orientation}
            $imgWidth={imgSize}
            $imgHeight={imgSize}
            $alignColumnContent={alignColumnContent}
          >
            {img && (
              <NextImage
                src={img.src}
                alt={img.alt}
                width={imgWidth}
                height={imgHeight}
              />
            )}
          </StyledImage>
          {showCheckmark && (
            <IconContainer
              $orientation={orientation}
              $mobileOrientation={mobileOrientation ?? orientation}
              $offsetBadge={offsetBadge}
            >
              {checked && (
                <NextImage
                  src={checkIcon}
                  width={28}
                  height={28}
                  alt={'Check icon'}
                />
              )}
            </IconContainer>
          )}
          <Info
            checked={checked}
            $orientation={orientation}
            $mobileOrientation={mobileOrientation ?? orientation}
          >
            <CardHeader
              $orientation={orientation}
              $mobileOrientation={mobileOrientation ?? orientation}
              $alignColumnContent={alignColumnContent}
            >
              <Name as={level} $typography={typography}>
                {title}
              </Name>
            </CardHeader>
            <Description>
              {unavailable ? unavailableText : description}
            </Description>
            {showMoreDetails && !unavailable && !outOfStock && (
              <MoreDetails
                type="button"
                unavailable={unavailable}
                onClick={e => {
                  e.stopPropagation();
                  onDetailsClick();
                }}
                $orientation={orientation}
                $mobileOrientation={mobileOrientation ?? orientation}
              >
                View Ingredients
              </MoreDetails>
            )}
          </Info>
        </CardContent>
      </Card>
    </CardWrapper>
  );

  return notSelectable && !unavailable && !outOfStock ? (
    <Tooltip trigger={getProductCard} placement="top" offsetY={-200}>
      {notSelectableText}
    </Tooltip>
  ) : (
    getProductCard()
  );
};

RecommendedProductCard.displayName = 'RecommendedProductCard';
RecommendedProductCard.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  unavailable: PropTypes.bool,
  unavailableText: PropTypes.string,
  notSelectable: PropTypes.bool,
  notSelectableText: PropTypes.string,
  outOfStock: PropTypes.bool,
  description: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  img: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  imgSize: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  badge: PropTypes.string,
  badgeVariant: PropTypes.string,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  orientation: PropTypes.oneOf(['row', 'column']),
  mobileOrientation: PropTypes.oneOf(['row', 'column']),
  offsetBadge: PropTypes.bool,
  width: PropTypes.number,
  mobileWidth: PropTypes.number,
  bgColor: PropTypes.oneOf(['base', 'lightest']),
  typography: PropTypes.string,
  showCheckmark: PropTypes.bool,
  alignColumnContent: PropTypes.string,
  borderRadius: PropTypes.number,
};
